import React from "react";
import type { FC } from "react";
import { memo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { ItemType, onDropType } from "../types";

export interface CardProps {
  keyNumber: number;
  id: string;
  moveCard: (id: string, to: number) => void;
  findCard: (id: string) => { index: number };
  children?: React.ReactNode;
  onDrop?: onDropType;
  disabled?: boolean;
  className?: string;
}

export const Card: FC<CardProps> = memo(function Card({
  id,
  moveCard,
  findCard,
  children,
  onDrop,
  disabled,
}) {
  const originalIndex = findCard(id).index;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "card",
      item: { id, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();

        if (!didDrop) {
          moveCard(droppedId, originalIndex);
        }
      },
    }),
    [id, originalIndex, moveCard],
  );

  const [, drop] = useDrop(
    () => ({
      accept: "card",
      hover({ id: draggedId }: ItemType) {
        if (draggedId !== id) {
          const { index: overIndex } = findCard(id);
          moveCard(draggedId, overIndex);
        }
      },
      drop({ originalIndex: startIndex }) {
        const { index: overIndex } = findCard(id);
        if (startIndex !== overIndex) {
          onDrop?.(overIndex, id);
        }
      },
    }),
    [findCard, moveCard],
  );

  const opacity = isDragging ? 0 : 1;

  return React.cloneElement(children as React.ReactElement, {
    ref: (node: any) => {
      if (!disabled) drag(drop(node));
    },
    style: { opacity },
  });
});

export default Card;
